import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Button } from 'reactstrap';
import AmbassadorDisplay from '../components/AmbassadorDisplay';
import ConvertToAmbassador from '../components/ConvertToAmbassador';
import { toast } from 'react-toastify';
import axios from 'axios';

const Ambassadors = ({ onLogout }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [isConvertOpen, setIsConvertOpen] = useState(false);
    const [selectedAmbassadors, setSelectedAmbassadors] = useState([]);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [removing, setRemoving] = useState(false);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    }

    const toggleConvertCanvas = () => {
        setIsConvertOpen(!isConvertOpen);
    }

    const handleConvertSuccess = () => {
        setRefreshKey(prevKey => prevKey + 1); // Refresh ambassador list
    }

    const handleRemoveAmbassadors = async () => {
        if (selectedAmbassadors.length === 0) {
            toast.warning('Please select at least one ambassador to remove');
            return;
        }

        setRemoving(true);
        try {
            const token = localStorage.getItem('accessToken');
            const userIds = selectedAmbassadors.map(a => a.id);
            const response = await axios.post(
                `${API_BASE_URL}/admin/ambassadors/remove`,
                { userIds },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            toast.success(response.data.message || `Successfully removed ${selectedAmbassadors.length} ambassador(s)`);
            setSelectedAmbassadors([]);
            setShowRemoveConfirm(false);
            setRefreshKey(prevKey => prevKey + 1); // Refresh ambassador list
        } catch (err) {
            console.error('Failed to remove ambassadors:', err);
            toast.error(err.response?.data?.error || 'Failed to remove ambassadors');
        } finally {
            setRemoving(false);
        }
    }

    return (
        <div className='d-flex w-100 overflow-hidden'>
            <div className='asidebar'>
                <Sidebar/>
            </div>
            <div className='main-content'>
                <div className='d-flex align-items-center justify-content-between mb-4'>
                    <h2>All Ambassadors</h2>
                    <div className='d-flex align-items-center justify-content-end'>
                        <div className="me-3">
                            <input
                                type="text"
                                className="form-control w-300px rounded-50 ps-3"
                                style={{height: '50px'}}
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-success rounded-pill px-4 me-2"
                            onClick={toggleConvertCanvas}
                            style={{
                                height: '50px',
                                fontWeight: '600',
                                border: 'none'
                            }}
                        >
                            Convert to Ambassador
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger rounded-pill px-4"
                            onClick={() => {
                                if (selectedAmbassadors.length === 0) {
                                    toast.warning('Please select ambassadors from the table to remove');
                                    return;
                                }
                                setShowRemoveConfirm(true);
                            }}
                            disabled={selectedAmbassadors.length === 0 || removing}
                            style={{
                                height: '50px',
                                fontWeight: '600',
                                border: 'none'
                            }}
                        >
                            {removing ? 'Removing...' : `Remove Ambassador${selectedAmbassadors.length > 1 ? 's' : ''} (${selectedAmbassadors.length})`}
                        </button>
                    </div>
                </div>
                
                <AmbassadorDisplay 
                    key={refreshKey} 
                    searchQuery={searchQuery}
                    selectedAmbassadors={selectedAmbassadors}
                    onSelectionChange={setSelectedAmbassadors}
                />

                <Offcanvas isOpen={isConvertOpen} toggle={toggleConvertCanvas} className="custom-canvas-width" direction='end'>
                    <OffcanvasHeader className='mb-4 mx-4 mt-2 border-bottom' toggle={toggleConvertCanvas}>
                        <span className='font-weight-bold'>Convert to Ambassador</span>
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        <ConvertToAmbassador onClose={toggleConvertCanvas} onSuccess={handleConvertSuccess} />
                    </OffcanvasBody>
                </Offcanvas>

                {/* Remove Confirmation Modal */}
                {showRemoveConfirm && (
                    <div 
                        className="modal fade show d-block" 
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        tabIndex="-1"
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Removal</h5>
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setShowRemoveConfirm(false)}
                                        disabled={removing}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p>Are you sure you want to remove ambassador privileges from the following {selectedAmbassadors.length} user(s)?</p>
                                    <ul className="list-unstyled">
                                        {selectedAmbassadors.map(amb => (
                                            <li key={amb.id} className="mb-1">
                                                <strong>{amb.fullName || amb.email}</strong> ({amb.email})
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-muted small mt-3">
                                        They will be converted back to regular users. Their referral history will be preserved.
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowRemoveConfirm(false)}
                                        disabled={removing}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger" 
                                        onClick={handleRemoveAmbassadors}
                                        disabled={removing}
                                    >
                                        {removing ? 'Removing...' : 'Confirm Remove'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Ambassadors;
